/**
 * Resolve o canal WhatsApp do ecommerce a partir do Endpoint FHIR.
 *
 * Só dispara a busca com organizationId. Enquanto carrega, a UI não deve
 * listar templates (evita double-fetch com WABA default e depois o real).
 *
 * Fallbacks Fluxmed (canal sem Meta):
 * - listagem: WABA 369757892878978
 * - envio: phoneNumberId 379190331933594 (ou VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { getEcommerceWhatsAppEndpointConfig } from "../services/ecommerceWhatsAppEndpointService";

/** WABA da plataforma Fluxmed quando não há Endpoint Meta. */
export const FLUXMED_DEFAULT_WABA_ID = "369757892878978";

/** phoneNumberId Fluxmed no path do FluxZap quando o Endpoint não traz número. */
export const FLUXMED_DEFAULT_PHONE_NUMBER_ID =
  import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID?.trim() ||
  "379190331933594";

export interface UseEcommerceWhatsAppEndpointResult {
  isLoading: boolean;
  hasActiveMetaEndpoint: boolean;
  wabaIdRank1: string | null;
  phoneNumberIdRank1: string | null;
  /** Número visível (contact.value rank 1). Não é o phoneNumberId. */
  displayPhoneRank1: string | null;
  /** WABA para GET message_templates (Endpoint ou default Fluxmed). */
  whatsappBusinessAccountId: string;
  /**
   * ID no path POST /fluxzap/{id}/{template}:
   * phoneNumberId rank 1 → WABA rank 1 → fallback Fluxmed.
   */
  fluxzapBusinessId: string;
  /** Aviso quando há Endpoint Meta sem IDs no rank 1. */
  channelWarning: string | null;
}

const EMPTY_ENDPOINT = {
  wabaIdRank1: null as string | null,
  phoneNumberIdRank1: null as string | null,
  displayPhoneRank1: null as string | null,
  hasActiveMetaEndpoint: false,
  fetchedOrganizationId: "",
};

export function useEcommerceWhatsAppEndpoint(
  organizationId?: string,
): UseEcommerceWhatsAppEndpointResult {
  const [hasTriggeredInitialLoad, setHasTriggeredInitialLoad] = useState(
    !organizationId?.trim(),
  );

  const loadTask = useCallback(async (trimmedOrganizationId: string) => {
    try {
      const config = await getEcommerceWhatsAppEndpointConfig(
        trimmedOrganizationId,
      );
      return { ...config, fetchedOrganizationId: trimmedOrganizationId };
    } catch (error) {
      console.error("useEcommerceWhatsAppEndpoint:", error);
      return {
        ...EMPTY_ENDPOINT,
        fetchedOrganizationId: trimmedOrganizationId,
      };
    }
  }, []);

  const {
    run: runLoad,
    loading,
    data,
    reset,
  } = useAsyncTask<[string], typeof EMPTY_ENDPOINT>({
    task: loadTask,
    initialData: EMPTY_ENDPOINT,
  });

  useEffect(() => {
    const trimmed = organizationId?.trim();
    if (!trimmed) {
      setHasTriggeredInitialLoad(true);
      reset();
      return;
    }

    let cancelled = false;
    setHasTriggeredInitialLoad(true);
    runLoad(trimmed).catch(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [organizationId, reset, runLoad]);

  const config = data ?? EMPTY_ENDPOINT;
  const organizationKey = organizationId?.trim() || "";
  const isChannelLoading =
    Boolean(organizationKey) &&
    (loading ||
      !hasTriggeredInitialLoad ||
      config.fetchedOrganizationId !== organizationKey);

  const whatsappBusinessAccountId =
    config.wabaIdRank1?.trim() || FLUXMED_DEFAULT_WABA_ID;

  const fluxzapBusinessId =
    config.phoneNumberIdRank1?.trim() ||
    config.wabaIdRank1?.trim() ||
    FLUXMED_DEFAULT_PHONE_NUMBER_ID;

  const channelWarning = useMemo(() => {
    if (isChannelLoading) return null;
    if (!config.hasActiveMetaEndpoint) return null;
    if (config.phoneNumberIdRank1?.trim() || config.wabaIdRank1?.trim()) {
      return null;
    }
    return "Endpoint Meta ativo sem número configurado (contato rank 1). O envio usará o canal padrão Fluxmed.";
  }, [
    config.hasActiveMetaEndpoint,
    config.phoneNumberIdRank1,
    config.wabaIdRank1,
    isChannelLoading,
  ]);

  return {
    isLoading: isChannelLoading,
    hasActiveMetaEndpoint: config.hasActiveMetaEndpoint,
    wabaIdRank1: config.wabaIdRank1,
    phoneNumberIdRank1: config.phoneNumberIdRank1,
    displayPhoneRank1: config.displayPhoneRank1,
    whatsappBusinessAccountId,
    fluxzapBusinessId,
    channelWarning,
  };
}
